'use client';

import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';

/**
 * Wraps any child with a magnetic pull toward the cursor within `radius` px.
 * Uses gsap.quickTo for near-zero latency — no React state updates per frame.
 * Skips on touch devices (hover: none).
 */
export default function MagneticButton({
  children,
  strength = 0.32,
  radius = 110,
}: {
  children: React.ReactNode;
  strength?: number;
  radius?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const xTo = useRef<(v: number) => void>();
  const yTo = useRef<(v: number) => void>();

  useEffect(() => {
    const el = ref.current;
    if (!el || !window.matchMedia('(hover: hover)').matches) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    xTo.current = gsap.quickTo(el, 'x', { duration: 0.55, ease: 'power2.out' }) as any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    yTo.current = gsap.quickTo(el, 'y', { duration: 0.55, ease: 'power2.out' }) as any;

    const onMove = (e: MouseEvent) => {
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = e.clientX - cx;
      const dy = e.clientY - cy;

      if (Math.hypot(dx, dy) < radius) {
        xTo.current?.(dx * strength);
        yTo.current?.(dy * strength);
      } else {
        xTo.current?.(0);
        yTo.current?.(0);
      }
    };

    const onLeave = () => {
      xTo.current?.(0);
      yTo.current?.(0);
    };

    window.addEventListener('mousemove', onMove, { passive: true });
    el.addEventListener('mouseleave', onLeave);
    return () => {
      window.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseleave', onLeave);
    };
  }, [strength, radius]);

  return (
    <div ref={ref} style={{ willChange: 'transform', display: 'inline-block' }}>
      {children}
    </div>
  );
}
